import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CellDoublingTimeCalculator = () => {
  const [cellData, setCellData] = useState({
    initialCellCount: '',
    finalCellCount: '',
    timeHours: '',
    growthMedium: '',
    temperature: '',
    co2Level: '',
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCellData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCellData(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
  };

  const calculateDoublingTime = () => {
    const { initialCellCount, finalCellCount, timeHours } = cellData;

    if (!initialCellCount || !finalCellCount || !timeHours) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const initialCount = parseFloat(initialCellCount);
      const finalCount = parseFloat(finalCellCount);
      const time = parseFloat(timeHours);

      if (isNaN(initialCount) || isNaN(finalCount) || isNaN(time)) {
        setError('Please enter valid numbers for all fields');
        return;
      }

      if (initialCount <= 0 || finalCount <= 0 || time <= 0) {
        setError('All values must be greater than zero');
        return;
      }

      const calculateDoublingTime = () => {
        const doublings = Math.log(finalCount / initialCount) / Math.log(2);
        const doublingTime = time / doublings;
        return doublingTime;
      };

      const calculateGrowthRate = () => {
        const growthRate = Math.log(finalCount / initialCount) / time;
        return growthRate;
      };

      const generateGrowthCurve = () => {
        const timePoints = Array.from({ length: 10 }, (_, i) => i * (time / 9));
        const theoreticalCounts = timePoints.map(t => 
          initialCount * Math.pow(2, (t / calculateDoublingTime()))
        );

        return {
          labels: timePoints,
          datasets: [
            {
              label: 'Theoretical Growth Curve',
              data: theoreticalCounts,
              borderColor: 'rgb(75, 192, 192)',
              tension: 0.1,
              fill: false
            }
          ]
        };
      };

      const resultsData = {
        initialCellCount: initialCount,
        finalCellCount: finalCount,
        timeHours: time,
        doublingTime: calculateDoublingTime().toFixed(2),
        growthRate: calculateGrowthRate().toFixed(4),
        generationTime: (calculateDoublingTime() / 24).toFixed(2) + ' days',
        growthPhase: determineGrowthPhase(Math.log(finalCount / initialCount) / Math.log(2)),
        optimizationTips: getOptimizationTips(cellData)
      };

      setResults(resultsData);
      setChartData(generateGrowthCurve());

    } catch (error) {
      setError('An error occurred while calculating doubling time');
    }
  };

  const determineGrowthPhase = (doublings) => {
    if (doublings < 1) return 'Lag Phase';
    else if (doublings < 5) return 'Log Phase';
    else if (doublings < 10) return 'Stationary Phase';
    else return 'Death Phase';
  };

  const getOptimizationTips = (cellData) => {
    const { growthMedium, temperature, co2Level } = cellData;
    const tips = [];

    if (growthMedium) {
      tips.push(
        'Ensure proper medium pH (7.2-7.4) for optimal growth',
        'Check for medium depletion and replenish if necessary',
        'Monitor osmolality for consistent growth'
      );
    }

    if (temperature) {
      tips.push(
        'Maintain temperature between 36-37°C for most mammalian cells',
        'Avoid temperature fluctuations that can affect growth',
        'Use water jacketed incubators for stable temperature'
      );
    }

    if (co2Level) {
      tips.push(
        'Maintain CO2 levels between 5-7% for most cell lines',
        'Monitor CO2 levels regularly',
        'Use humidified incubators to prevent medium evaporation'
      );
    }

    tips.push(
      'Regularly monitor cell density and viability',
      'Pass cells before they reach confluence',
      'Maintain proper cell-to-medium ratio',
      'Regularly check for contamination',
      'Document growth patterns for future reference'
    );

    return tips;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-blue-100 to-green-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Cell Doubling Time Calculator | Cell Growth Analysis Tool</title>
          <meta name="description" content="Calculate cell doubling time and growth rate with our advanced calculator. Analyze cell growth kinetics and receive optimization tips for your cell culture conditions." />
          <meta name="keywords" content="cell doubling time calculator, cell growth rate, cell culture analysis, growth phase determination, cell biology calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb- max-w-2xl mx-auto">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">🔬</div>
            <h1 className="text-3xl font-bold text-gray-800">Cell Doubling Time Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Cell Doubling Time Calculator helps you analyze cell growth kinetics and provides:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Doubling time calculations</li>
              <li>Growth rate analysis</li>
              <li>Growth phase determination</li>
              <li>Optimization recommendations</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Initial Cell Count:</label>
              <input
                type="number"
                name="initialCellCount"
                value={cellData.initialCellCount}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter initial cell count"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Final Cell Count:</label>
              <input
                type="number"
                name="finalCellCount"
                value={cellData.finalCellCount}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter final cell count"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Time (hours):</label>
              <input
                type="number"
                name="timeHours"
                value={cellData.timeHours}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter time in hours"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Growth Medium:</label>
              <input
                type="text"
                name="growthMedium"
                value={cellData.growthMedium}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter growth medium type"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Temperature (°C):</label>
              <input
                type="number"
                name="temperature"
                value={cellData.temperature}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter temperature"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">CO2 Level (%):</label>
              <input
                type="number"
                name="co2Level"
                value={cellData.co2Level}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter CO2 percentage"
              />
            </div>

            <div className="input-group">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="showDetailedAnalysis"
                  checked={cellData.showDetailedAnalysis}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-5 w-5 text-green-600 mr-2"
                />
                <span className="text-gray-700">Show detailed growth analysis and optimization tips</span>
              </label>
            </div>
          </div>

          <button
            onClick={calculateDoublingTime}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Doubling Time
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Cell Growth Analysis</h2>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Doubling Time</h3>
                  <p className="text-xl font-bold">{results.doublingTime} hours</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Generation Time</h3>
                  <p className="text-xl font-bold">{results.generationTime}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Growth Rate</h3>
                  <p className="text-xl font-bold">{results.growthRate} doublings/hour</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Growth Phase</h3>
                  <p className="text-xl font-bold">{results.growthPhase}</p>
                </div>
              </div>

              {chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Cell Count'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Time (hours)'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Theoretical Growth Curve'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Optimization Tips</h3>
                <ul className="list-disc pl-6 space-y-2">
                  {results.optimizationTips.map((tip, index) => (
                    <li key={index} className="text-gray-600">{tip}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Understanding Cell Doubling Time Section */}
        <div className="calculator-section mt-12 ">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Cell Doubling Time <span className="text-blue-600">📊</span></h2>
          <div className="text-gray-600">
            <p>Cell doubling time is a crucial metric in cell culture that helps in:</p>
            <ul className="list-disc pl-6 mt-4">
              <li>Optimizing Culture Conditions: Adjusting growth medium and environmental parameters</li>
              <li>Monitoring Cell Health: Identifying growth phase and potential issues</li>
              <li>Scaling Up Cultures: Planning for bioreactor operations</li>
              <li>Research Applications: Validating experimental results</li>
            </ul>
            <p className="italic mt-4">Note: Optimal doubling time varies by cell line (typically 12-24 hours for mammalian cells)</p>
          </div>
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12 ">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Cell Doubling Time Calculator <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Cell Doubling Time Calculator effectively:</p>
            
            <div className="step-section mt-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Collect Your Data</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Count initial cells before passaging</li>
                <li>Count final cells after desired time period</li>
                <li>Record time between counts in hours</li>
                <li>Note growth conditions (medium, temperature, CO2)</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Enter Data</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Input initial and final cell counts</li>
                <li>Enter time duration in hours</li>
                <li>Add growth conditions information</li>
                <li>Check detailed analysis option for more insights</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Review doubling time and generation time</li>
                <li>Check growth rate for consistency</li>
                <li>Identify current growth phase</li>
                <li>Follow optimization tips for improvement</li>
              </ul>
            </div>

            <div className="step-section mt-6 bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Normal doubling time varies by cell line</li>
                <li>Monitor cell viability alongside counts</li>
                <li>Consider environmental factors</li>
                <li>Regularly check for contamination</li>
                <li>Document growth patterns for consistency</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CellDoublingTimeCalculator;